import Styles from './modal.module.css';
import React, { useRef, useState } from 'react';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { IoPersonOutline } from 'react-icons/io5';
import { ContactData, photoUrl } from './shared';

type ContactModalProps = {
  companyId: string;
  recruitId: string;
  initial: ContactData;
  onClose: () => void;
  onSaved: (next: ContactData) => void;
};

// 「見学の担当者」編集モーダル。10.salonPr のスタッフ追加モーダルと同じ作り。
// 保存先は recruits/{recruitId}.visitInfo.contact（ドット表記で1キーのみ更新）。
// 写真は Storage の visitContact/{companyId} に素で保存し、photoUpdatedAt(ms)で
// キャッシュバストする。
const ContactModal: React.FC<ContactModalProps> = ({ companyId, recruitId, initial, onClose, onSaved }) => {

  const bodyRef = useRef<HTMLDivElement | null>(null);

  const [ name, setName ] = useState(initial.name);
  const [ role, setRole ] = useState(initial.role);
  const [ message, setMessage ] = useState(initial.message);
  const [ photoUpdatedAt ] = useState(initial.photoUpdatedAt);

  const [ photoFile, setPhotoFile ] = useState<File | null>(null);
  const [ photoPreview, setPhotoPreview ] = useState('');

  const [ errorImage, setErrorImage ] = useState(false);
  const [ errorName, setErrorName ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState('');
  const [ saving, setSaving ] = useState(false);

  const currentPhoto = photoPreview || (photoUpdatedAt > 0 ? photoUrl(companyId, photoUpdatedAt) : '');

  const showError = (msg: string) => {
    setErrorMessage(msg);
    bodyRef.current?.scrollTo({ top: 0 });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setErrorImage(false);
  };

  async function save() {
    setErrorImage(false);
    setErrorName(false);
    setErrorMessage('');

    if (!currentPhoto) {
      setErrorImage(true);
      showError('担当者の顔写真を設定してください。');
      return;
    }
    if (name.trim() === '') {
      setErrorName(true);
      showError('担当者の名前を入力してください。');
      return;
    }

    setSaving(true);
    try {
      let nextPhotoUpdatedAt = photoUpdatedAt;
      if (photoFile) {
        const storage = getStorage();
        await uploadBytes(ref(storage, `visitContact/${companyId}`), photoFile);
        nextPhotoUpdatedAt = Date.now();
      }

      const next: ContactData = {
        name: name.trim(),
        role: role.trim(),
        message: message.trim(),
        photoUpdatedAt: nextPhotoUpdatedAt,
      };

      const db = getFirestore();
      await updateDoc(doc(db, 'recruits', recruitId), { 'visitInfo.contact': next });

      onSaved(next);
      onClose();
    } catch (error) {
      console.error('担当者情報の保存に失敗しました', error);
      showError('保存に失敗しました。時間をおいて再度お試しください。');
      setSaving(false);
    }
  }

  return (
    <div className={Styles.overlay} role="dialog" aria-modal="true">
      <div className={Styles.scrim} onClick={() => !saving && onClose()} />
      <div className={Styles.card}>

        <div className={Styles.header}>
          <h3 className={Styles.headerTitle}>見学の担当者</h3>
          <button type="button" className={Styles.headerBtn} onClick={save} disabled={saving}>
            {saving ? '保存中…' : '保存'}
          </button>
        </div>

        <div className={Styles.body} ref={bodyRef}>

          {errorMessage && (
            <div className={Styles.errorBox} role="alert">
              <p className={Styles.errorBox_label}>エラーメッセージ：</p>
              <p className={Styles.errorBox_text}>{errorMessage}</p>
            </div>
          )}

          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>顔写真<span className={Styles.req}>※</span></span>
            </div>
            <label className={errorImage ? `${Styles.imageBox} ${Styles.imageBox_error}` : Styles.imageBox}>
              {currentPhoto ? (
                <img className={Styles.imagePreview} src={currentPhoto} alt="担当者の顔写真" />
              ) : (
                <div className={Styles.imagePlaceholder}>
                  <IoPersonOutline className={Styles.imagePlaceholderIcon} />
                  <span>写真を追加</span>
                </div>
              )}
              <input type="file" accept="image/*" className={Styles.hiddenInput} onChange={handleImageChange} disabled={saving} />
            </label>
          </div>

          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>名前<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{name.length} / 20</span>
            </div>
            <input
              className={errorName ? `${Styles.input} ${Styles.input_error}` : Styles.input}
              placeholder="山田 花子"
              maxLength={20}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
            />
          </div>

          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>役職<span className={Styles.optional}>任意</span></span>
              <span className={Styles.count}>{role.length} / 20</span>
            </div>
            <input
              className={Styles.input}
              placeholder="採用担当・店長 など"
              maxLength={20}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={saving}
            />
          </div>

          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>一言<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{message.length} / 40</span>
            </div>
            <textarea
              className={`${Styles.input} ${Styles.textarea}`}
              placeholder="気軽にご連絡ください！！"
              maxLength={40}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={saving}
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default ContactModal;
